import React, { useState } from 'react';
import {
  Box,
  Heading,
  Grid,
  Divider,
  Text,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';

const AccountSecurity = () => {
  const toast = useToast();

  // State to track which section is expanded ("password" or null)
  const [expandedSection, setExpandedSection] = useState(null);

  // Reset Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Toggle expanded section for password change
  const clearPasswordFields = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const togglePasswordSection = () => {
    if (expandedSection === 'password') {
      setExpandedSection(null);
      clearPasswordFields();
    } else {
      setExpandedSection('password');
    }
  };

  // Handler: Change Password
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setPasswordError('La nuova password e la conferma non corrispondono.');
      return;
    }
    setIsChangingPassword(true);
    try {
      await axios.post('/api/users/me/change-password', {
        currentPassword,
        newPassword,
      });
      toast({
        title: 'Password Aggiornata',
        description: 'La tua password è stata aggiornata con successo.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setExpandedSection(null);
      clearPasswordFields();
    } catch (error) {
      console.error(error);
      setPasswordError(
        error.response?.data?.message ||
          'Impossibile aggiornare la password. Riprova.'
      );
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <Box
      as="span"
      bg="#2f3136"
      p={{ base: 6, md: 8 }}
      display="block"
      borderRadius="20px"
      boxShadow="0 20px 40px rgba(0,0,0,0.3)"
      border="1px solid"
      borderColor="gray.600"
      minH={{ base: 'unset', md: '532px' }}
    >
      <Heading
        size="lg"
        mb={4}
        id="sicurezza-title"
        color="white"
        fontWeight="800"
      >
        Sicurezza Account
      </Heading>
      <Divider mb={6} borderColor="gray.600" />

      {expandedSection !== 'password' && (
        <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
          {/* Reset Password Card */}
          <Flex
            p={4}
            direction="column"
            bg="#36393f"
            borderRadius="12px"
            boxShadow="sm"
            border="1px solid"
            borderColor="gray.600"
            cursor="pointer"
            onClick={togglePasswordSection}
            _hover={{
              borderColor: '#ffcc00',
              transform: 'translateY(-2px)',
            }}
            transition="all 0.3s ease"
          >
            <Heading size="md" mb={2} color="white" fontWeight="700">
              Cambia Password
            </Heading>
            <Text mb={4} color="gray.300">
              Aggiorna la tua password frequentemente per mantenere il tuo
              account sicuro.
            </Text>
            <Button
              bg={expandedSection === 'password' ? '#e73341' : '#ffcc00'}
              color={expandedSection === 'password' ? 'white' : 'black'}
              alignSelf="flex-end"
              borderRadius="12px"
              fontWeight="600"
              data-testid="change-password-button"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
              }}
              transition="all 0.3s ease"
            >
              Cambia password
            </Button>
          </Flex>
        </Grid>
      )}

      {/* Expanded Change Password Section */}
      {expandedSection === 'password' && (
        <Box
          mt={6}
          p={4}
          bg="#36393f"
          maxW="md"
          borderRadius="12px"
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.600"
        >
          <Heading size="md" mb={4} color="white" fontWeight="700">
            Cambia Password
          </Heading>
          <FormControl mb={4} isRequired>
            <FormLabel color="gray.300">Password Attuale</FormLabel>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Inserisci la password attuale"
              data-clarity-mask="True"
              bg="#2f3136"
              color="white"
              borderColor="gray.600"
              _placeholder={{ color: 'gray.400' }}
              _focus={{
                borderColor: '#ffcc00',
                boxShadow: '0 0 0 1px #ffcc00',
              }}
            />
          </FormControl>
          <FormControl mb={4} isRequired>
            <FormLabel color="gray.300">Nuova Password</FormLabel>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Inserisci la nuova password"
              data-clarity-mask="True"
              bg="#2f3136"
              color="white"
              borderColor="gray.600"
              _placeholder={{ color: 'gray.400' }}
              _focus={{
                borderColor: '#ffcc00',
                boxShadow: '0 0 0 1px #ffcc00',
              }}
            />
          </FormControl>
          <FormControl mb={4} isRequired isInvalid={!!passwordError}>
            <FormLabel color="gray.300">Conferma Nuova Password</FormLabel>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Conferma la nuova password"
              data-clarity-mask="True"
              bg="#2f3136"
              color="white"
              borderColor="gray.600"
              _placeholder={{ color: 'gray.400' }}
              _focus={{
                borderColor: '#ffcc00',
                boxShadow: '0 0 0 1px #ffcc00',
              }}
            />
            <FormErrorMessage>{passwordError}</FormErrorMessage>
          </FormControl>
          <Flex justify="flex-end">
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                setExpandedSection(null);
                clearPasswordFields();
              }}
              color="gray.300"
              _hover={{ bg: 'gray.600' }}
            >
              Chiudi
            </Button>
            <Button
              bg="#ffcc00"
              color="black"
              onClick={handleChangePassword}
              isLoading={isChangingPassword}
              borderRadius="12px"
              fontWeight="600"
              _hover={{
                bg: '#e6b800',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
              }}
              transition="all 0.3s ease"
            >
              Salva
            </Button>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default AccountSecurity;
